import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import Copy from '@react-spectrum/s2/icons/Copy';
import OpenIn from '@react-spectrum/s2/icons/OpenIn';
import { useCallback, useState, type ReactNode } from 'react';

import {
	PROVIDERS,
	copyImportPrompt,
	openProviderPopup,
	type Provider,
} from './providers';

type MemoryImportPasteFormProps = {
	provider: Provider;
	websiteUrl?: string | null;
	instagramHandle?: string | null;
	// Re-seeds the textarea when the user navigates back here: the wizard
	// remounts screens on every transition, so local state doesn't survive.
	initialSnippet?: string;
	onSubmit: (snippet: string) => void;
	/** Rendered after the submit button (e.g. a Skip or Cancel link). */
	secondaryAction?: ReactNode;
};

const labelStyle: React.CSSProperties = {
	textTransform: 'uppercase',
	letterSpacing: '0.12em',
	fontSize: 12,
	fontWeight: 600,
	color: 'var(--text-muted)',
};

/**
 * Exported so the settings Memories card's step indicator can reuse the same
 * numbered-circle look instead of duplicating it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const stepNumberStyle: React.CSSProperties = {
	flex: 'none',
	width: 26,
	height: 26,
	borderRadius: '50%',
	background: '#1A1A1A',
	color: 'white',
	fontSize: 13,
	fontWeight: 700,
	display: 'grid',
	placeItems: 'center',
	marginTop: 1,
};

const kbdStyle: React.CSSProperties = {
	fontFamily: 'monospace',
	fontSize: 13,
	background: 'rgba(0,0,0,0.05)',
	border: '1px solid rgba(0,0,0,0.08)',
	borderRadius: 5,
	padding: '1px 6px',
};

/**
 * The "run the prompt in the provider, paste the answer back" form: numbered
 * instructions, copy-again / reopen helpers, textarea, and submit. Shared
 * between the onboarding paste screen and the settings Memories card.
 */
export const MemoryImportPasteForm = ({
	provider,
	websiteUrl,
	instagramHandle,
	initialSnippet,
	onSubmit,
	secondaryAction,
}: MemoryImportPasteFormProps) => {
	const [pasted, setPasted] = useState(initialSnippet ?? '');
	const [copied, setCopied] = useState(false);

	const { label } = PROVIDERS[provider];
	const vars = {
		websiteUrl: websiteUrl ?? '',
		instagramHandle: instagramHandle ?? '',
	};
	const canSubmit = pasted.trim().length > 12;

	const handleCopyAgain = useCallback(() => {
		copyImportPrompt(provider, vars);
		setCopied(true);
		setTimeout(() => setCopied(false), 1800);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [provider, vars.websiteUrl, vars.instagramHandle]);

	const handleReopen = useCallback(() => {
		copyImportPrompt(provider, vars);
		openProviderPopup(PROVIDERS[provider].chatUrl);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [provider, vars.websiteUrl, vars.instagramHandle]);

	const handleSubmit = useCallback(() => {
		if (!canSubmit) return;
		onSubmit(pasted.trim());
	}, [canSubmit, pasted, onSubmit]);

	return (
		<>
			<p
				className="mb-6"
				style={{
					fontSize: 18,
					lineHeight: 1.5,
					color: 'var(--text-primary)',
				}}
			>
				I just opened {label} and copied the prompt for you. Run it there, then
				bring the answer back here.
			</p>

			<div className="mb-6 flex flex-col" style={{ gap: 16 }}>
				<div className="flex" style={{ gap: 14 }}>
					<div style={stepNumberStyle}>1</div>
					<div
						style={{ fontSize: 16, lineHeight: 1.5, paddingTop: 2 }}
						className="flex flex-col items-start"
					>
						<span>
							In {label}, <strong>paste</strong>{' '}
							<span style={kbdStyle}>⌘V</span> and hit send — I already copied
							the prompt for you.
						</span>
						<button
							type="button"
							onClick={handleCopyAgain}
							className="mt-2 inline-flex items-center"
							style={{
								gap: 6,
								fontSize: 13,
								fontWeight: 500,
								color: copied ? 'var(--text-primary)' : 'var(--text-muted)',
								background: 'rgba(0,0,0,0.03)',
								border: '1px solid rgba(0,0,0,0.08)',
								borderRadius: 8,
								padding: '6px 11px',
								cursor: 'pointer',
							}}
						>
							<Copy />
							{copied ? 'Copied!' : 'Copy the prompt again'}
						</button>
					</div>
				</div>
				<div className="flex" style={{ gap: 14 }}>
					<div style={stepNumberStyle}>2</div>
					<div style={{ fontSize: 16, lineHeight: 1.5, paddingTop: 2 }}>
						{label} replies with a <strong>block of text</strong>. Tap the{' '}
						<strong>copy icon</strong> in its corner.
					</div>
				</div>
				<div className="flex" style={{ gap: 14 }}>
					<div style={stepNumberStyle}>3</div>
					<div style={{ fontSize: 16, lineHeight: 1.5, paddingTop: 2 }}>
						<strong>Paste it below</strong> and I&apos;ll take it from here.
					</div>
				</div>
			</div>

			<div className="mb-2 flex flex-col" style={{ gap: 8 }}>
				<span style={labelStyle}>Paste it here</span>
				<textarea
					aria-label="Paste your export"
					value={pasted}
					onChange={(event) => setPasted(event.target.value)}
					placeholder="Paste here — ⌘V"
					rows={6}
					className="w-full resize-none rounded-lg border border-[#c9c6bf]
						bg-white px-3.5 py-3 text-[14px] leading-relaxed
						text-(--text-primary) shadow-[0_1px_2px_rgba(20,16,12,0.04)]
						transition-[border-color,box-shadow] outline-none
						placeholder:text-(--text-muted) focus:border-[#3b63fb]
						focus:shadow-[0_0_0_3px_rgba(59,99,251,0.18)]"
				/>
			</div>

			<button
				type="button"
				onClick={handleReopen}
				className="mb-6 inline-flex items-center"
				style={{
					gap: 7,
					fontSize: 13,
					color: 'var(--text-muted)',
					background: 'none',
					border: 'none',
					padding: 0,
					cursor: 'pointer',
				}}
			>
				<OpenIn
					UNSAFE_style={
						{ '--iconPrimary': 'var(--text-muted)' } as React.CSSProperties
					}
				/>
				<span style={{ textDecoration: 'underline' }}>Open {label} again</span>
			</button>

			<div className="flex items-center" style={{ gap: 20 }}>
				<Button
					variant="primary"
					staticColor="black"
					fillStyle="fill"
					size="L"
					isDisabled={!canSubmit}
					onPress={handleSubmit}
				>
					<Text>Add memories</Text>
					<ChevronRight />
				</Button>
				{secondaryAction}
			</div>
		</>
	);
};
